import { useCallback, useEffect, useReducer, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AppBarTitle } from '@components/app-bar-title';
import { useCompanyMaster, usePintMaster, useRequest } from '@lib/common';
import { useSnackbar } from 'notistack';
import CompanyForm from './form/company-form';
import SpellcheckRoundedIcon from '@material-ui/icons/SpellcheckRounded';
import { IconButton, Chip, Link } from '@material-ui/core';
import { PageLoading } from '@components/page-loading';
import * as companyService from '@api/company';
// import * as logsService from '@api/logs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { FadeInContainer } from '@components/fadein-container';
import LaunchIcon from '@material-ui/icons/Launch';
import { BreakLine } from '@components/breakline';
import { CompanyEdit, getAllQuotesAndPoliciesByVtrId, getCompanyServices } from './utils';
import { Prompt } from 'react-router'
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  Grid,
  Typography,
  List,
  Tooltip
} from '@material-ui/core';
import { useDefaultImage } from '@lib/common';
import { useCompanyLoader } from 'services/company-dataloader.service';
import { Skeleton } from '@material-ui/lab';
import { useMemo } from 'react';
import { capitalCase, useRecentInjector, useServerPagination, vtrUrl } from '@lib/common';
import styled from 'styled-components';
import { windowObj } from '@lib/common';
import { http } from '@api/client';
import { paramterize, pickResponse, pickResult } from '@api/utils';
import { DELI_BASE_URL_V1 } from '@api/client';
import { pink, blue, deepOrange } from '@material-ui/core/colors';
import { TimeAgo } from '@components/time-ago';
import { isValid } from 'date-fns';
import TimelapseTwoToneIcon from '@material-ui/icons/TimelapseTwoTone';
import { ColoredAvatar, ColoredChip } from '@components/colored-elements';
import { forkJoin, of } from 'rxjs';
import { fetchReputationByVtrId } from '@api/company-reputation';
 import { useLocation } from 'react-router-dom';
import { getMatchedCompanyType, getMatchedServices } from '@pages/company-matching/search/utils';


export const CreateCompanyPage = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();
  const { master: masterTypes } = useCompanyMaster('types');
  const { master: masterServices } = useCompanyMaster('services');
  const location = useLocation();
    
  const prefillData = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const key = params.get('prefillKey');
    if (!key) return null;

    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    // 🔹 Map company type
    const mappedTypes =
      parsed.categories
        ?.map((type: string) =>
          getMatchedCompanyType(type, masterTypes)
        )
        .filter(Boolean) || [];

    // 🔹 Map services
    const mappedServices =
      getMatchedServices(parsed.clientServices || [], masterServices);

    return {
      ...parsed,
      categories: mappedTypes,
      clientServices: mappedServices,
    };
  }, [location.search, masterTypes, masterServices]);

  // Form Actions
  const createCompany = useCallback(company => {
    const infoNotification = enqueueSnackbar('Please wait Creating Company', { variant: 'info' });
    delete company.vtr_id;
    return companyService
      .create(company)
      .pipe(finalize(() => closeSnackbar(infoNotification)))
      .subscribe({
        next: res => {
          enqueueSnackbar('Company Created Successfully!', { variant: 'success' });
          // enqueueSnackbar('Refreshing Data on PINT... Please wait!', { variant: 'warning' });
          if (windowObj.RAZZLE_CALL_COMPANY_REFRESH_API == 'Y') {
            companyService.refreshCompanyOnPINT(res['data']['vtr_id']).subscribe({
              next: res => {
              },
              error: err => {
              },
              complete: () => {
              },
            });
          }
          history.push(`/company/search`);
        },
        error: err => {
          if (err?.response?.status === 409) {
            const vtr_id_matching = err?.response?.data?.error.split("'")[1];
            return enqueueSnackbar(
              <DuplicateError
                id={vtr_id_matching}
                text="The company you are trying to create already exists,"
              />,
              {
                variant: 'warning',
                autoHideDuration: 7000,
              }
            );
          }
          enqueueSnackbar(`Error Creating company (${err?.response?.data?.error})`, {
            variant: 'error',
          });
        },
      });
  }, []);

  return (
    <div>
      <AppBarTitle>
        <IconButton onClick={null} color="inherit" edge="start">
          <SpellcheckRoundedIcon />
        </IconButton>
        New Company
      </AppBarTitle>
      <CompanyForm onFinish={createCompany}   defaultValue={prefillData as any}/>
    </div>
  );
};
export interface ReducerState {
  okPressed: boolean;
}
export const InitialState: ReducerState = { okPressed: false };

export function EditCompanyPage() {

  const { id } = useParams<{ id: string }>();
  const { loading: loading_badges, master: reputation } = usePintMaster('reputation_badge');

  const badges = useMemo(() => (Array.isArray(reputation) ? reputation : []), [reputation]);
  /*   const { loading: companyLoading, result: company } = useRequest(
    () => companyService.get(id),
    [id]
  );
  */
  const { loading: companyLoading, result: companyRaw } = useRequest(
    () =>
      companyService.get(id).pipe(
        switchMap((companyRes: any) => {
          const companyData = companyRes;
          const vtrId = companyData?.vtr_id;

          const sortedServices = [...(companyData?.company_services || [])].sort(
            (a, b) => a.order_id - b.order_id,
          );

          const serviceIdsParam = sortedServices.map(s => s.id).join(',');

          const hierarchy$ =
            sortedServices.length > 0 ? getCompanyServices(serviceIdsParam, sortedServices) : of([]);
          if (!vtrId) {
            return of({
              ...companyData,
              quotes_policies: [],
              reputation_score: null,
              company_services: sortedServices,
            });
          }

          return forkJoin({
            quotesPolicies: getAllQuotesAndPoliciesByVtrId(vtrId).pipe(
              catchError(err => {
                console.error('Quotes API failed', err);
                return of(null); // fallback
              }),
            ),

            reputation: fetchReputationByVtrId(vtrId).pipe(
              catchError(err => {
                console.error('Reputation API failed', err);

                // trigger snackbar here
                enqueueSnackbar?.('Reputation data failed to load', {
                  variant: 'error',
                });

                return of(null); // fallback
              }),
            ),

            company_services: hierarchy$.pipe(
              catchError(err => {
                console.error('Hierarchy API failed', err);
                return of([]); // fallback
              }),
            ),
          }).pipe(
            map(({ quotesPolicies, reputation, company_services }) => {
             // console.log('Fetched company details:', {
               // companyData,
               // quotesPolicies,
            //    reputation,
               // company_services,
            //  });
                const overrideScore = reputation?.override_reputation_score;
                const computedScore = reputation?.computed_reputation_score;

                const reputationScore =
                  overrideScore && overrideScore !== 0
                    ? overrideScore
                    : computedScore ?? null;

              return {
                ...companyData,
                quotes_policies: quotesPolicies?.data?.data || [],
                reputation_score: reputationScore || null,
                company_services: company_services || [],
              };
            }),
          );
        }),
      ),
    [id],
  );

  // ✅ Merge here
  const company = useMemo(() => {
    if (!companyRaw) return null;
    return { ...companyRaw, badges };
  }, [companyRaw, badges]);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();

  const redirectToSearch = useCallback(() => {
    if (history.location.search) {
      const returnTo = new URLSearchParams(history.location.search).get('returnTo');
      if (returnTo) {
        history.push(returnTo);
        return;
      }
    }
    history.push('/company/search');
  }, [history]);

  // Form Actions
  const editCompany = useCallback(
    udpCompany => {

      const infoNotification = enqueueSnackbar('Please wait updating Company', { variant: 'info' });
      companyService.get(id).pipe().subscribe(
        (response) => {
          udpCompany.media_and_services = response.media_and_services; 
          return companyService
            .update(id, udpCompany)
            .pipe(finalize(() => closeSnackbar(infoNotification)))
            .subscribe({
              next: res => {
                enqueueSnackbar('Company updated Successfully!', { variant: 'success' });
                // enqueueSnackbar('Refreshing Data on PINT... Please wait!', { variant: 'warning' });
                if (windowObj.RAZZLE_CALL_COMPANY_REFRESH_API == 'Y') {
                  companyService.refreshCompanyOnPINT(res['data']['vtr_id']).subscribe({
                    next: res => {
                      // enqueueSnackbar('Refresh done successfully on PINT!', { variant: 'success' });
                    },
                    error: err => {
                      // enqueueSnackbar(`Error refreshing data on PINT`, {
                      //   variant: 'error',
                      // });
                      // redirectToSearch();
                    },
                    complete: () => {
                      // redirectToSearch();
                    },
                  });
                }
                redirectToSearch();
              },
              error: err => {
                if (err?.response?.status === 409) {
                  const vtr_id_matching = err?.response?.data?.error.split("'")[1];
                  return enqueueSnackbar(
                    <DuplicateError
                      id={vtr_id_matching}
                      text="The company cannot be updated as the edited company already exists,"
                    />,
                    {
                      variant: 'warning',
                      autoHideDuration: 7000,
                    }
                  );
                }
                enqueueSnackbar(
                  `Error updating company (${err?.response?.data?.error || 'Internal error, please report this error'
                  })`,
                  {
                    variant: 'error',
                  }
                );
              },
            });
        }
      );
    },
    [enqueueSnackbar, history, id]
  );
  const modifiedTime = useMemo(() => {
    if (company) {
      const time = new Date(company.modified_at || company.created_at);
      return isValid(time) && time;
    }
    return false;
  }, [company]);
  return (
    <div>
      <AppBarTitle>
        <Grid container spacing={3}>
          <Grid item style={{ alignSelf: 'center' }}>
            <IconButton onClick={null} color="inherit" edge="start">
              <SpellcheckRoundedIcon />
            </IconButton>
            Edit Company
          </Grid>

          <Grid item>
            <CompanyDataStyle>
              <CompanyData id={id} company={company} loading={companyLoading} />
            </CompanyDataStyle>
          </Grid>
          {company?.ingestion_method == 'SelfService' ?
            <Grid item>
              <CompanyDataStyle>
                <Grid item>
                  {/* <Tooltip title="Owned by SelfService"> */}
                  <List style={{ margin: 0, padding: 0 }}>

                    <ListItem style={{ marginTop: 4, padding: 0 }}>

                      <ListItemText style={{ margin: 0, padding: 0, fontSize: 10 }} secondary={modifiedTime && (
                        <Grid item>
                          <>
                            <Tooltip title="SelfService Updated Time">
                              <ColoredChip
                                colored={deepOrange[500]}
                                size="small"
                                // variant="outlined"
                                label="SelfService"
                              />
                            </Tooltip>
                            <Tooltip title="SelfService Updated Time">
                              <ColoredChip
                                colored={blue[500]}
                                size="small"
                                // variant="outlined"
                                label={<TimeAgo style={{ fontSize: 10, fontWeight: 'normal' }} time={modifiedTime} />}
                              />
                            </Tooltip>
                          </>
                        </Grid>
                      )} />
                    </ListItem>
                    <ListItem style={{ margin: 0, padding: 0 }}>

                      <ListItemText style={{ fontSize: 10 }} secondary={<Tooltip title="SelfService Updated User">
                        <ColoredChip
                          colored={blue[500]}
                          size="small"
                          // variant="outlined"
                          label={company?.updated_by}
                        />
                      </Tooltip>} />
                    </ListItem>

                  </List>
                  {/* </Tooltip> */}
                </Grid>
              </CompanyDataStyle>
            </Grid>
            : ''}
        </Grid>
      </AppBarTitle>
      {companyLoading && !company ? (
        <PageLoading>Loading Company to edit...</PageLoading>
      ) : (
        <FadeInContainer $anime=".5s">
          <CompanyForm edit defaultValue={company as any} logs={[]} onFinish={editCompany} />
        </FadeInContainer>
      )}

    </div>

  );
}

/**
 * Show company Data on header during edit company
 *
 */
const CompanyData = ({ id, company, loading }) => {
  const srcList = useMemo(() => {
    return [`${windowObj.RAZZLE_S3_BUCKET_URL}/${id}.png`, '/images/company_default.svg'].filter(
      e => e
    );
  }, []);
  const { src: fallbackedSource } = useDefaultImage({
    srcList,
    defaultSrc: '/images/company_default.svg',
    useSuspense: false,
  });
  return company ? (
    <>
      <Grid item>
        <CompanyEdit
          name={company.vtr_company_name || ''}
          vtrId={id}
          type="COMPANY"
          avatarImg={fallbackedSource}
          companyData={company}
        />
      </Grid>
    </>
  ) : (
    <Skeleton
      style={{ display: 'block' }}
      variant="rect"
      width={200}
      height={70}
      animation="wave"
    />
  );
};


/**
 * Company Duplicate error.
 */
const DuplicateError = ({ id, text = 'The company you are trying to create already exists,' }) => (
  <div>
    {text}
    <BreakLine n={1} />
    <Chip
      size="small"
      style={{ background: 'white', marginTop: 3 }}
      component={Link}
      target="_blank"
      href={`/company/edit/${id}`}
      icon={<LaunchIcon />}
      label={`ID: ${id}`}
      clickable
    />
  </div>
);

const CompanyDataStyle = styled.div`
  .MuiTypography-colorTextSecondary {
    color: white;
  }
  .MuiListItemAvatar-root {
    display: none !important;
  }
  .MuiSvgIcon-colorPrimary {
    color: white;
  }
`;
